import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button as BaseButton,
  FlexColumn,
  FlexSpace as BaseFlexSpace,
  Loader,
  Search,
  Table,
  FlexCenter
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useClientLocations from '@ubeya/shared/hooks/account/useClientLocations';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import { useField } from 'react-final-form';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import { ContextMenu } from '@ubeya/shared-web/components';
import { isUndefined, omitBy } from 'lodash';
import ClientLocationModal from 'settings/components/BranchSettings/Locations/ClientLocationModal';

const ActionsRenderer = ({ rowData, columnData: { onEdit } }) => {
  const options = useTranslatedOptions([{ label: 'edit', onClick: () => onEdit(rowData) }]);

  return <ContextMenu options={options} usePortal />;
};

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 31px 50px;
  overflow: auto;
  min-height: 400px;

  .loader {
    z-index: 1;
  }
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 500;
`;

const Container = styled(FlexColumn)`
  flex: 1;
  min-width: 500px;
`;

const ActionContainer = styled(FlexCenter)``;

const Button = styled(BaseButton)`
  margin-left: 10px;

  justify-content: flex-end;
  margin-left: 16px;
`;

const FlexSpace = styled(BaseFlexSpace)`
  margin-bottom: 20px;
`;

const Locations = ({ clientId, disableFieldsEdit = true }) => {
  const { t } = useContext(ConfigContext);
  const {
    mappedClientBranchesLocations,
    loading: isLoading,
    addClientLocation,
    updateClientLocation
  } = useClientLocations({ clientId });

  const {
    input: { value: branches }
  } = useField('branches');

  const mappedClientBranches = (branches || []).reduce((output, current) => {
    output[current.id] = current;
    return output;
  }, {});

  const clientBranchesOptions = (branches || [])
    .map(({ id, name }) => ({
      value: id,
      label: name
    }))
    ?.sort(SORT_BY_STRING('label'));

  const [selectedLocation, setSelectedLocation] = useState();

  const [search, setSearch] = useState('');

  const allLocations = useMemo(
    () =>
      Object.entries(mappedClientBranchesLocations || {}).reduce((output, [branchId, locations]) => {
        return output.concat(locations.map((location) => ({ ...location, branchId: Number(branchId) })));
      }, []),
    [mappedClientBranchesLocations]
  );

  const filteredLocations = useMemo(
    () =>
      (allLocations || []).filter(
        ({ name, address, isSubLocation }) =>
          !isSubLocation &&
          ((name || '').toLowerCase().includes(search.toLowerCase()) ||
            (address || '').toLowerCase().includes(search.toLowerCase()))
      ),
    [allLocations, search]
  );

  const showBranchName = Object.keys(mappedClientBranches).length > 1;

  const columns = useMemo(
    () => [
      showBranchName && {
        Header: t('branchName'),
        dataKey: 'branchId',
        formatter: (value) => mappedClientBranches?.[value]?.name || '',
        maxWidth: 350
      },
      {
        Header: t('locationName'),
        dataKey: 'name',
        maxWidth: 350
      },
      {
        Header: t('address'),
        dataKey: 'address'
      }
    ],
    [mappedClientBranches, showBranchName, t]
  );

  const onConfirm = useCallback(
    async (location) => {
      const clientBranchId = location.branchId || branches[0]?.id;

      await (selectedLocation?.id
        ? updateClientLocation({ id: selectedLocation.id, clientBranchId, ...omitBy(location, isUndefined) })
        : addClientLocation({ clientBranchId, location }));
    },
    [branches, selectedLocation?.id, updateClientLocation, addClientLocation]
  );

  return (
    <Wrapper>
      <FlexSpace>
        <Title>{t('locations')}</Title>
        <ActionContainer>
          <Search value={search} onChange={setSearch} />
          <Button type="button" secondary narrow onClick={() => setSelectedLocation({})}>
            {t('addLocation')}
          </Button>
        </ActionContainer>
      </FlexSpace>

      <Container>
        {isLoading ? (
          <Loader />
        ) : (
          <Table
            columns={columns}
            data={filteredLocations}
            shadow={false}
            onRowClick={({ rowData }) => setSelectedLocation(rowData)}
            actionRenderer={({ rowData }) => (
              <ActionsRenderer
                rowData={rowData}
                columnData={{
                  onEdit: setSelectedLocation
                }}
              />
            )}
          />
        )}
      </Container>

      {selectedLocation && (
        <ClientLocationModal
          editableAddress
          selectedLocationId={selectedLocation.id}
          onClose={() => setSelectedLocation()}
          onConfirm={onConfirm}
          branchesOptions={clientBranchesOptions}
          showBranchSelector={clientBranchesOptions.length > 1}
          clientId={clientId}
          clientBranchId={selectedLocation.branchId}
          disableFieldsEdit={disableFieldsEdit}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(Locations);
